import { AppointmentStatusLabel, BackBreadcrumbItem } from '@eluve/blocks';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbSeparator,
  Button,
  DetailsSidebarTrigger,
  Header,
  Icon,
  SidebarTrigger,
  Stack,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@eluve/components';
import { ChatWithPatientData } from '@eluve/feature-chat-with-patient-data';
import {
  useAppointmentHeaderDetails,
  useAppointmentId,
  useAppointmentName,
} from '@eluve/frontend-appointment-hooks';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';
import { TenantOrUserFeatureFlaggedComponent } from '@eluve/smart-blocks';

import { appConfig } from '../../../../../../config';
import { NewAppointmentButton } from '../../components/NewAppointmentButton';
import { OverviewBreadcrumbItem } from '../../components/OverviewBreadcrumbItem';

export const AppointmentHeader: React.FC = () => {
  const appointmentHeaderDetails = useAppointmentHeaderDetails();
  const appointmentName = useAppointmentName();
  const appointmentId = useAppointmentId();
  const tenantId = useAssignedTenantIdFromParams();

  return (
    <Header>
      <Stack gap={2} direction="horizontal">
        <SidebarTrigger />
        <Breadcrumb className="flex-1 overflow-hidden">
          <BreadcrumbList>
            <BackBreadcrumbItem />
            <OverviewBreadcrumbItem />
            <BreadcrumbSeparator className="hidden sm:block">
              /
            </BreadcrumbSeparator>
            <BreadcrumbItem className="hidden truncate text-text-primary sm:block">
              {appointmentName}
            </BreadcrumbItem>
            <BreadcrumbItem className="hidden truncate pl-2 text-text-primary sm:block">
              {appointmentHeaderDetails && (
                <AppointmentStatusLabel
                  isSigned={appointmentHeaderDetails.isSigned}
                  isSummarized={appointmentHeaderDetails.isSummarized}
                  status={appointmentHeaderDetails.status}
                  isAmending={appointmentHeaderDetails.isAmending}
                  isAmended={appointmentHeaderDetails.isAmended}
                  size="xs"
                />
              )}
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </Stack>
      <Stack justify="end" fullWidth={false} gap={2} direction="horizontal">
        <TenantOrUserFeatureFlaggedComponent flag="INSIGHTS_CHAT">
          {appointmentHeaderDetails?.patientId ? (
            <ChatWithPatientData
              // If patient is switched on an appointment, we want to remount the component
              // to prevent previous threads be applied for new patient
              key={`${appointmentHeaderDetails.patientId}-${appointmentId}`}
              tenantId={tenantId}
              patientId={appointmentHeaderDetails.patientId}
              baseUrl={appConfig.VITE_API_DOMAIN}
              appointmentId={appointmentId}
            />
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  disabled
                  aria-label="Patient Insights"
                >
                  <Icon name="MessageSquare" size="auto" />
                </Button>
              </TooltipTrigger>
              <TooltipContent align="end" alignOffset={-8}>
                Please select a patient first!
              </TooltipContent>
            </Tooltip>
          )}
        </TenantOrUserFeatureFlaggedComponent>
        <NewAppointmentButton />
        <DetailsSidebarTrigger>
          <Button variant="outline" aria-label="Appointment details">
            <Icon name="User" size="auto" />
          </Button>
        </DetailsSidebarTrigger>
      </Stack>
    </Header>
  );
};
